export type MultipartResumeSessionV1 = {
  version: 1;
  contentHash: string;
  fileId: string;
  uploadId: string;
  fileName: string;
  fileSize: number;
  contentType: string;
  partSize: number;
  partCount: number;
  lastPartSize: number;
  completedParts: Record<number, string>;
  needsConfirm?: boolean;
  createdAtMs: number;
  updatedAtMs: number;
};

const STORAGE_KEY = 'omniscribe_multipart_resume_sessions_v1';
const MAX_SESSIONS = 10;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function loadAll(): Record<string, MultipartResumeSessionV1> {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, MultipartResumeSessionV1>;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed;
  } catch {
    return {};
  }
}

function saveAll(map: Record<string, MultipartResumeSessionV1>) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore (quota/private mode)
  }
}

function prune(map: Record<string, MultipartResumeSessionV1>) {
  const now = Date.now();
  const entries = Object.entries(map)
    .filter(([, s]) => s && s.version === 1 && typeof s.updatedAtMs === 'number' && now - s.updatedAtMs <= MAX_AGE_MS)
    .sort(([, a], [, b]) => (b.updatedAtMs ?? 0) - (a.updatedAtMs ?? 0));

  const kept = entries.slice(0, MAX_SESSIONS);
  return Object.fromEntries(kept);
}

export function listMultipartResumeSessions(): MultipartResumeSessionV1[] {
  const all = prune(loadAll());
  return Object.values(all);
}

export function getMultipartResumeSession(contentHash: string): MultipartResumeSessionV1 | null {
  const normalized = contentHash?.trim().toLowerCase();
  if (!normalized) return null;
  const all = prune(loadAll());
  return all[normalized] ?? null;
}

export function upsertMultipartResumeSession(session: MultipartResumeSessionV1): void {
  if (!session || session.version !== 1) return;
  const key = session.contentHash?.trim().toLowerCase();
  if (!key) return;

  const all = prune(loadAll());
  all[key] = session;
  saveAll(prune(all));
}

export function deleteMultipartResumeSession(contentHash: string): void {
  const key = contentHash?.trim().toLowerCase();
  if (!key) return;
  const all = loadAll();
  if (!(key in all)) return;
  delete all[key];
  saveAll(all);
}
